import { type PhysicsTuning, gearRatios } from '../config/tuning';
import { torqueFactor } from '../sim/car';
import { clamp } from '../sim/dmath';

/**
 * Racing line + speed profile used by the bot driver (and for braking zones).
 * The line starts as the shortest path through the corridor (Laplacian
 * smoothing) and is then relaxed toward minimum curvature (bi-Laplacian), which
 * gives the classic outside-apex-outside shape. All deterministic.
 */
export interface RacingLine {
  /** Lateral offset from the centerline per sample, left positive. */
  offset: number[];
  x: number[];
  y: number[];
  kappa: number[];
  /** Distance from sample i to i+1 along the line. */
  ds: number[];
  /** The road's slope and vertical curvature at each sample (set once the day's hills are known). */
  grade?: number[];
  vcurv?: number[];
  /** v3 circuits: the road's banking (slope across it, left side higher positive), and the load floor over crests (in g). */
  bank?: number[];
  minLoad?: number;
}

export interface CorridorInput {
  n: number;
  px: number[];
  py: number[];
  tx: number[];
  ty: number[];
}

export function computeRacingLine(c: CorridorInput, halfWidth: number, margin: number): RacingLine {
  const n = c.n;
  const limit = halfWidth - margin;
  let off = new Array<number>(n).fill(0);
  const nx = c.ty.map((v) => -v);
  const ny = c.tx;

  const pointX = (o: number[], i: number): number => c.px[i] + nx[i] * o[i];
  const pointY = (o: number[], i: number): number => c.py[i] + ny[i] * o[i];

  // 1) Shortest-path-like relaxation at decreasing scales.
  for (const [k, iters] of [
    [20, 40],
    [10, 50],
    [5, 50],
  ] as const) {
    for (let it = 0; it < iters; it++) {
      const next = new Array<number>(n);
      for (let i = 0; i < n; i++) {
        const a = (i - k + n) % n;
        const b = (i + k) % n;
        const mx = 0.5 * (pointX(off, a) + pointX(off, b));
        const my = 0.5 * (pointY(off, a) + pointY(off, b));
        const target = (mx - c.px[i]) * nx[i] + (my - c.py[i]) * ny[i];
        next[i] = clamp(off[i] + (target - off[i]) * 0.5, -limit, limit);
      }
      off = next;
    }
  }

  // 2) Minimum-curvature relaxation (bi-Laplacian) at decreasing scales.
  for (const [k, iters] of [
    [12, 120],
    [6, 160],
    [3, 160],
    [1, 120],
  ] as const) {
    for (let it = 0; it < iters; it++) {
      const next = new Array<number>(n);
      for (let i = 0; i < n; i++) {
        const i1 = (i - k + n) % n;
        const i2 = (i - 2 * k + 2 * n) % n;
        const j1 = (i + k) % n;
        const j2 = (i + 2 * k) % n;
        const bx = pointX(off, i2) - 4 * pointX(off, i1) + 6 * pointX(off, i) - 4 * pointX(off, j1) + pointX(off, j2);
        const by = pointY(off, i2) - 4 * pointY(off, i1) + 6 * pointY(off, i) - 4 * pointY(off, j1) + pointY(off, j2);
        const g = bx * nx[i] + by * ny[i];
        next[i] = clamp(off[i] - g * 0.045, -limit, limit);
      }
      off = next;
    }
  }

  const x = new Array<number>(n);
  const y = new Array<number>(n);
  for (let i = 0; i < n; i++) {
    x[i] = pointX(off, i);
    y[i] = pointY(off, i);
  }
  const ds = new Array<number>(n);
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const dx = x[j] - x[i];
    const dy = y[j] - y[i];
    ds[i] = Math.sqrt(dx * dx + dy * dy);
  }
  return { offset: off, x, y, kappa: lineCurvature(x, y, 2), ds };
}

/** Signed curvature from the circumcircle of (i-k, i, i+k), lightly smoothed. */
function lineCurvature(x: number[], y: number[], k: number): number[] {
  const n = x.length;
  const raw = new Array<number>(n);
  for (let i = 0; i < n; i++) {
    const a = (i - k + n) % n;
    const b = (i + k) % n;
    const ax = x[a] - x[i];
    const ay = y[a] - y[i];
    const bx = x[b] - x[i];
    const by = y[b] - y[i];
    const cross = ax * by - ay * bx;
    const la = Math.sqrt(ax * ax + ay * ay);
    const lb = Math.sqrt(bx * bx + by * by);
    const cx = x[b] - x[a];
    const cy = y[b] - y[a];
    const lc = Math.sqrt(cx * cx + cy * cy);
    // kappa = 2 sin(angle)/chord = 2 cross / (la lb lc); sign: left turn positive.
    raw[i] = -(2 * cross) / (la * lb * lc);
  }
  const out = new Array<number>(n);
  for (let i = 0; i < n; i++) {
    out[i] = (raw[(i - 1 + n) % n] + 2 * raw[i] + raw[(i + 1) % n]) * 0.25;
  }
  return out;
}

/** Best straight-line acceleration the car can manage at each whole m/s. */
export function accelTable(p: PhysicsTuning, maxSpeed = 100): number[] {
  const out: number[] = [];
  const ratios = gearRatios(p);
  const g = 9.81;
  const L = p.cgToFront + p.cgToRear;
  for (let v = 0; v <= maxSpeed; v++) {
    let best = 0;
    for (const gr of ratios) {
      const ratio = gr * p.finalDrive;
      let rpm = (v / p.rearWheelRadius) * ratio * 9.549296585513721;
      if (rpm >= p.redlineRpm) continue;
      if (rpm < p.idleRpm) rpm = p.idleRpm;
      const f = (p.enginePeakTorque * torqueFactor(rpm / p.redlineRpm) * ratio * p.drivetrainEfficiency) / p.rearWheelRadius;
      if (f > best) best = f;
    }
    // Rear traction with some weight transfer under acceleration, plus downforce.
    const trac = p.tireMu * p.rearGrip * (p.mass * g * (p.cgToFront / L) + p.mass * 5 * (p.cgHeight / L) + p.downforceRear * v * v) * p.frictionEllipse * 0.97;
    const f = best < trac ? best : trac;
    out.push((f - p.dragCoef * v * v - p.rollingResistance * v) / p.mass);
  }
  return out;
}

export interface SpeedProfileOptions {
  mu: number;
  brakeDecel: number;
  accel: number[];
  vTop: number;
  /** Downforce per unit mass, (dfFront + dfRear) / mass: more grip the faster you go. */
  downforce?: number;
  /** Per sample: rise per metre, and vertical curvature (positive in dips). Absent = flat. */
  grade?: number[];
  vcurv?: number[];
  /** v3: banking per sample (slope across the road, left higher positive), and the sim's load floor over crests (g). */
  bank?: number[];
  minLoad?: number;
}

export function speedProfile(line: RacingLine, o: SpeedProfileOptions): number[] {
  const n = line.x.length;
  const g = 9.81;
  const v = new Array<number>(n);
  const kd = o.mu * (o.downforce ?? 0);
  for (let i = 0; i < n; i++) {
    // m v^2 kappa = mu (m g + m v^2 kv + D v^2)  =>  v^2 = mu g / (kappa - mu (D/m + kv)):
    // a dip (kv > 0) adds grip, a crest takes it away.
    const k = Math.abs(line.kappa[i]);
    let vl: number;
    if (o.minLoad !== undefined) {
      // v3: banking leaning into the corner lends a hand and presses the car down;
      // over a crest the load never drops below the sim's floor, so a straight crest never limits.
      const b = o.bank ? o.bank[i] : 0;
      const into = line.kappa[i] >= 0 ? -b : b;
      const cb = 1 / Math.sqrt(1 + b * b);
      const sb = into * cb;
      const kv = o.vcurv ? o.vcurv[i] : 0;
      // The sim's load is clamped to [minLoad g, 2 g] (downforce comes on top): the corner speed is
      // the uncapped one, never above what 2 g of load allows, never below what the floor allows.
      const den = k - o.mu * (k * sb + kv) - kd;
      const v1 = den > 1e-6 ? Math.sqrt((g * (o.mu * cb + sb)) / den) : o.vTop;
      const denC = k - kd;
      const vCap = denC > 1e-6 ? Math.sqrt((g * (2 * o.mu + sb)) / denC) : o.vTop;
      const vF = denC > 1e-6 ? Math.sqrt((g * (o.mu * o.minLoad + sb)) / denC) : o.vTop;
      const vUp = v1 < vCap ? v1 : vCap;
      vl = vUp > vF ? vUp : vF;
    } else {
      const den = k - kd - (o.vcurv ? o.mu * o.vcurv[i] : 0);
      vl = den > 1e-6 ? Math.sqrt((o.mu * g) / den) : o.vTop;
    }
    v[i] = vl < o.vTop ? vl : o.vTop;
  }
  const acc = (s: number): number => {
    const i = Math.floor(s);
    if (i >= o.accel.length - 1) return o.accel[o.accel.length - 1];
    return o.accel[i] + (o.accel[i + 1] - o.accel[i]) * (s - i);
  };
  for (let pass = 0; pass < 2; pass++) {
    for (let q = n - 1; q >= 0; q--) {
      const j = (q + 1) % n;
      // Uphill helps the brakes, downhill works against them.
      const dec = o.brakeDecel + (o.grade ? 9.81 * o.grade[q] : 0);
      const lim = Math.sqrt(v[j] * v[j] + 2 * (dec > 1 ? dec : 1) * line.ds[q]);
      if (lim < v[q]) v[q] = lim;
    }
    for (let q = 0; q < n; q++) {
      const j = (q + 1) % n;
      const e = v[q] * v[q] + 2 * (acc(v[q]) - (o.grade ? 9.81 * o.grade[q] : 0)) * line.ds[q];
      const lim = Math.sqrt(e > 0 ? e : 0);
      if (lim < v[j]) v[j] = lim;
    }
  }
  return v;
}

export function profileLapTime(line: RacingLine, v: number[]): number {
  let t = 0;
  const n = v.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    t += line.ds[i] / Math.max(0.5 * (v[i] + v[j]), 1);
  }
  return t;
}
